import { createPublicApiClient, type PublicApiClient } from '@cloneai/api-client';
import { logger, metrics } from '@cloneai/observability';
import type { AgentPublicConfig } from '@cloneai/shared-types';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { env } from '../env.js';
import type { SessionRecorder } from '../voice/sessionRecorder.js';

export interface TextChatMessage {
  readonly id: string;
  readonly role: 'user' | 'assistant';
  readonly text: string;
  readonly timestamp: string;
}

export interface TextChatState {
  readonly messages: readonly TextChatMessage[];
  readonly isSending: boolean;
  readonly isInputBlocked: boolean;
  readonly error: string | null;
  readonly sendMessage: (message: string) => Promise<void>;
}

export interface UseTextChatOptions {
  readonly canSubmit?: boolean;
  readonly onTurnStarted?: () => void;
  readonly onAnimationQueued?: (animationId: string, renderDeadlineAt: string) => void;
}

let apiClientSingleton: PublicApiClient | null = null;
function getApiClient(): PublicApiClient {
  if (!apiClientSingleton) {
    apiClientSingleton = createPublicApiClient({ baseUrl: env.VITE_API_BASE_URL });
  }
  return apiClientSingleton;
}

function makeId(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function useTextChat(
  config: AgentPublicConfig | null,
  tenantSlug: string | null,
  agentSlug: string | null,
  lang: string | null,
  isActive: boolean,
  recorderRef: Readonly<{ current: SessionRecorder | null }>,
  options: UseTextChatOptions = {},
): TextChatState {
  const [messages, setMessages] = useState<TextChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeRef = useRef(false);
  const requestInFlightRef = useRef(false);
  const scopeKey =
    config && tenantSlug && agentSlug && lang && isActive
      ? `${config.agentId}:${tenantSlug}:${agentSlug}:${lang}`
      : null;
  const scopeKeyRef = useRef(scopeKey);
  scopeKeyRef.current = scopeKey;
  const canSubmit = options.canSubmit ?? true;
  const onTurnStarted = options.onTurnStarted;
  const onAnimationQueued = options.onAnimationQueued;

  useEffect(() => {
    if (!isActive) return;
    if (activeRef.current) return;
    activeRef.current = true;
    setMessages([]);
    setError(null);
  }, [isActive, config?.agentId, lang]);

  useEffect(() => {
    if (isActive) return;
    activeRef.current = false;
    requestInFlightRef.current = false;
    setIsSending(false);
  }, [isActive]);

  const sendMessage = useCallback(
    async (rawMessage: string): Promise<void> => {
      const message = rawMessage.trim();
      if (
        !message ||
        !config ||
        !tenantSlug ||
        !agentSlug ||
        !lang ||
        !isActive ||
        !canSubmit ||
        requestInFlightRef.current
      ) {
        return;
      }

      const recorder = recorderRef.current;
      if (!recorder) return;

      requestInFlightRef.current = true;
      onTurnStarted?.();

      const userMessage: TextChatMessage = {
        id: makeId(),
        role: 'user',
        text: message,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsSending(true);
      setError(null);

      const startedAt = performance.now();
      const requestScopeKey = scopeKey;
      const tags = { tenantSlug, agentSlug, lang, agentId: config.agentId };
      try {
        await recorder.start();
        const credentials = await recorder.getSessionCredentials();
        if (!credentials) throw new Error('Session is not available.');

        const response = await getApiClient().publicSessions.sendTextTurn(
          credentials.sessionId,
          {
            lang,
            message,
            clientTurnId: userMessage.id,
          },
          { sessionToken: credentials.sessionToken },
        );
        if (scopeKeyRef.current !== requestScopeKey) {
          metrics.increment('agent.text_chat.turn.discarded', 1, {
            tags: { reason: 'scope-changed' },
          });
          return;
        }
        setMessages((prev) => [
          ...prev,
          {
            id: makeId(),
            role: 'assistant',
            text: response.reply,
            timestamp: new Date().toISOString(),
          },
        ]);
        const queuedAnimation = response.animation;
        if (queuedAnimation?.kind === 'queued') {
          onAnimationQueued?.(queuedAnimation.animationId, queuedAnimation.renderDeadlineAt);
        }
        metrics.increment('agent.text_chat.turn.completed', 1, { tags });
        metrics.timing('agent.text_chat.turn.duration', performance.now() - startedAt, { tags });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Text chat failed.';
        logger.error('agent text chat turn failed', { ...tags, message: errorMessage });
        metrics.increment('agent.text_chat.turn.failed', 1, { tags });
        recorder.recordImmediateEvent({
          type: 'error',
          timestamp: new Date().toISOString(),
          payload: { code: 'text-chat-turn-failed', message: errorMessage },
        });
        Sentry.captureException(err);
        setError(errorMessage);
      } finally {
        requestInFlightRef.current = false;
        setIsSending(false);
      }
    },
    [
      agentSlug,
      canSubmit,
      config,
      isActive,
      lang,
      onAnimationQueued,
      onTurnStarted,
      recorderRef,
      scopeKey,
      tenantSlug,
    ],
  );

  return { messages, isSending, isInputBlocked: isSending || !canSubmit, error, sendMessage };
}
